import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Input from '@repo/ui/input'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getOtoIndividualPropositionPricing } from '../../../../api/enrollments/get-oto-individual-proposition-pricing'
import { checkPromoCode } from '../../../../api/promo-codes/check-promocode'
import { otoIndividualTransferMethodEnumMapping } from '../../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { PromoCodeApplicationEnum } from '../../../../lib/types/enums/promo-code/promo-code-application.enum'
import { OtoIndividualPropositionInput } from '../../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { PromoCode } from '../../../../lib/types/models/promo-code/promo-code'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import DetailsEditCard from '../../../elements/details-edit-card'
import OtoIndividualPriceDetails from '../oto-individual-price-details'
import {
  getOtoIndividualReturnDate,
  getOtoIndividualWeeksLabel,
  getRegionOfStayLabel
} from '../utils/oto-individual-utils'

type Props = {
  input: OtoIndividualPropositionInput
  // the backend resolves the age limits of the région de séjour and of its lieu d'arrivée
  // against it, and prices a mandatory option / accueil-transfert whatever the input says
  childBirthDate?: string
  // opens the popup on the given tab (0 = séjour selection, 1 = details/options)
  onEdit?: (tabIndex: number) => void
  // called when the frais de dossier / réduction famille checkboxes change
  onInputChange?: (input: OtoIndividualPropositionInput) => void
  // opens the insurance selection; the card is only shown when provided
  onEditInsurances?: () => void
}

const OtoIndividualPropositionSummary = ({
  input,
  childBirthDate,
  onEdit,
  onInputChange,
  onEditInsurances
}: Props) => {
  // the backend sends date-only strings, which `new Date` would read as UTC midnight
  const departureDate = DateFormatter.toDate(input.departureDate)
  const returnDate = input.returnDate
    ? DateFormatter.toDate(input.returnDate)
    : getOtoIndividualReturnDate(departureDate, input.numberOfWeeks)

  // undefined = untouched, so the applied code shows; '' = cleared by the user
  const [promoCodeState, setPromoCodeState] = useState<{ value?: string; error?: string }>({})
  const promoCodeValue = promoCodeState.value ?? input.promoCode?.code ?? ''

  const includeApplicationFees = input.pricing?.includeApplicationFees ?? true
  const includeFamilyDiscount = input.pricing?.includeFamilyDiscount ?? false

  // every amount comes from the backend pricing engine — no price math on the frontend
  const pricingRequest = {
    numberOfWeeks: input.numberOfWeeks,
    departureDate: input.departureDate,
    regionOfStayId: input.regionOfStay.id,
    ...(input.withOption && { optionId: input.regionOfStay.option.id }),
    transferMethod: input.transferMethod,
    pricing: { includeApplicationFees, includeFamilyDiscount },
    ...(input.insurances?.length && {
      insuranceIds: input.insurances.map((insurance) => insurance.id)
    }),
    ...(input.promoCode && { promoCode: input.promoCode.code }),
    ...(childBirthDate && { childBirthDate })
  }

  const {
    data: pricing,
    isPending: isPricingPending,
    isError: isPricingError
  } = useQuery({
    queryKey: ['oto-individual-proposition-pricing', pricingRequest],
    queryFn: () => getOtoIndividualPropositionPricing(pricingRequest)
  })

  // the selection is priced with the code before it is applied: the backend refuses a code that
  // would make the total negative, and its message is shown while the code stays unapplied
  const { mutate: applyPromoCodeMutation, isPending: isApplyPromoCodePending } = useMutation({
    mutationFn: (promoCode: PromoCode) =>
      getOtoIndividualPropositionPricing({ ...pricingRequest, promoCode: promoCode.code }).then(
        () => promoCode
      ),
    onSuccess: (promoCode) => {
      setPromoCodeState({})
      onInputChange?.({
        ...input,
        promoCode: { code: promoCode.code, discount: promoCode.discount, type: promoCode.type }
      })
    },
    onError: showAxiosError
  })

  const { mutate: checkPromoCodeMutation, isPending: isCheckPromoCodePending } = useMutation({
    mutationFn: checkPromoCode,
    onSuccess: (promoCode) => applyPromoCodeMutation(promoCode),
    onError: () =>
      setPromoCodeState((prev) => ({ ...prev, error: 'Le code avantage saisi est invalide' }))
  })

  // an automatic promo code is applied by the backend on save, so the field is only offered
  // when the applied code is a manual one
  const hasAutomaticPromoCode = input.promoCode?.application === PromoCodeApplicationEnum.AUTOMATIC

  // an age limit can make the option or the accueil-transfert mandatory: the backend then prices
  // them whatever the selection said, so what it priced is what is displayed
  const isOptionIncluded = pricing ? pricing.options.length > 0 : input.withOption
  const effectiveTransferMethod = pricing?.transferMethod ?? input.transferMethod

  return (
    <div className="grid grid-cols-3 gap-4 mt-5">
      <div className="col-span-2 flex flex-col gap-3">
        <DetailsEditCard title={'Séjour'} disabled={!onEdit} onEditClick={() => onEdit?.(0)}>
          <div className="flex flex-col gap-2">
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Pays
            </Typography>
            <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
              {input.product.country.name}
            </Typography>
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Dates et durée de séjour
            </Typography>
            <div className="flex gap-1">
              <Badge
                text={`${DateFormatter.DDMMYYYY(departureDate)} - ${DateFormatter.DDMMYYYY(returnDate)}`}
              />
              <Badge variant="warning" text={getOtoIndividualWeeksLabel(input.numberOfWeeks)} />
            </div>
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Formule
            </Typography>
            <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
              {input.product.name}
            </Typography>
            {/* the rest of the selection made in the popup */}
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Heures de cours / semaine
            </Typography>
            <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
              {`${input.numberOfHours}h`}
            </Typography>
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Région du séjour
            </Typography>
            <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
              {getRegionOfStayLabel(input.regionOfStay)}
            </Typography>
            <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
              Lieu d'arrivée
            </Typography>
            <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
              {input.arrivalPlace.title}
            </Typography>
            <div className="h-4" />
          </div>
        </DetailsEditCard>

        <div className="flex flex-row gap-3">
          <div className="flex-1">
            <DetailsEditCard title={'Options'} disabled={!onEdit} onEditClick={() => onEdit?.(1)}>
              <div className="flex flex-col gap-2">
                <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                  Option(s) facultative(s)
                </Typography>
                <Typography variant="tag">
                  {isOptionIncluded ? getTranslatedName(input.regionOfStay.option) : '-'}
                </Typography>
                {pricing?.optionIsMandatory && (
                  <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                    Cette option est obligatoire pour l’âge du participant.
                  </Typography>
                )}
                <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                  Accueil-transfert
                </Typography>
                <Typography variant="tag">
                  {otoIndividualTransferMethodEnumMapping[effectiveTransferMethod]}
                </Typography>
                {pricing?.transferIsMandatory && (
                  <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                    L’accueil-transfert est obligatoire pour l’âge du participant.
                  </Typography>
                )}
                <div className="h-4" />
              </div>
            </DetailsEditCard>
          </div>

          {onEditInsurances && (
            <div className="flex-1">
              <DetailsEditCard title={'Assurance(s)'} onEditClick={onEditInsurances}>
                <div className="flex flex-col gap-2">
                  <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                    Assurance(s) souscrite(s)
                  </Typography>
                  {input.insurances?.length ? (
                    input.insurances.map((insurance) => (
                      <Badge key={insurance.id} text={insurance.name} />
                    ))
                  ) : (
                    <Typography variant="tag">-</Typography>
                  )}

                  <div className="h-4" />
                </div>
              </DetailsEditCard>
            </div>
          )}
        </div>
      </div>

      <OtoIndividualPriceDetails
        pricing={pricing}
        isPending={isPricingPending}
        isError={isPricingError}
        fallbackOptionTitle={getTranslatedName(input.regionOfStay.option)}
        includeApplicationFees={includeApplicationFees}
        includeFamilyDiscount={includeFamilyDiscount}
        onToggleApplicationFees={
          onInputChange &&
          (() =>
            onInputChange({
              ...input,
              pricing: { ...input.pricing, includeApplicationFees: !includeApplicationFees }
            }))
        }
        onToggleFamilyDiscount={
          onInputChange &&
          (() =>
            onInputChange({
              ...input,
              pricing: { ...input.pricing, includeFamilyDiscount: !includeFamilyDiscount }
            }))
        }
      >
        {onInputChange && !hasAutomaticPromoCode && (
          <InnerBox className="p-3 rounded-xl mt-3">
            <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
              Appliquer un code avantage
            </Typography>
            <div className="h-1" />
            <Input
              type="text"
              errorMessage={promoCodeState.error}
              label="Code avantage"
              value={promoCodeValue}
              onChange={(e) => setPromoCodeState({ value: e.target.value, error: undefined })}
              suffix={
                <div className="flex items-center gap-2">
                  {promoCodeValue && (
                    <button
                      type="button"
                      className="bg-white-active rounded-sm w-5 h-5 flex items-center justify-center"
                      onClick={() => {
                        setPromoCodeState({ value: '', error: undefined })
                        // nothing was applied: clearing the field alone must not save
                        if (input.promoCode) onInputChange({ ...input, promoCode: undefined })
                      }}
                    >
                      <XIcon width="15" height="15" color={tailwindColors.grey.lighter} />
                    </button>
                  )}
                  <Button
                    label="Appliquer"
                    variant="link"
                    type="button"
                    loading={isCheckPromoCodePending || isApplyPromoCodePending}
                    disabled={!promoCodeValue}
                    onClick={() =>
                      checkPromoCodeMutation({
                        code: promoCodeValue,
                        productJourneyPlaceId: input.regionOfStay.id
                      })
                    }
                  />
                </div>
              }
            />
          </InnerBox>
        )}
      </OtoIndividualPriceDetails>
    </div>
  )
}

export default OtoIndividualPropositionSummary
